import React, { useState, useContext, useEffect } from 'react';
import Participant from '../Participant/Participant';
import { styled } from '@material-ui/core/styles';
import useParticipants from '../../hooks/useParticipants/useParticipants';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import useSelectedParticipant from '../VideoProvider/useSelectedParticipant/useSelectedParticipant';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import InviteModal from '../InviteModal/InviteModal';
import { MeetingContext } from '../../App';
import { ToolBarContext } from '../../App';
import ParticipantSwitchOverModal from './ParticipantSwitchOverModal.tsx/ParticipantSwitchOverModal';
import useMainSpeaker from '../../hooks/useMainSpeaker/useMainSpeaker';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

const Container = styled('aside')(({ theme }) => ({
  [theme.breakpoints.down('xs')]: {
    overflowY: 'initial',
    overflowX: 'auto',
    padding: 0,
    display: 'flex',
  },
  padding: '0.5em',
  overflowY: 'auto',
}));

const ScrollContainer = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('xs')]: {
    display: 'flex',
  },
}));

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    inviteButton: {
      position: 'fixed',
      bottom: 10,
      backgroundColor: '#1e88e5',
      width: `calc(${theme.sidebarWidth - 15}px)`,
      color: 'white',
      zIndex: 1000,
      '&:hover': {
        backgroundColor: '#1e88e5',
      },
    },
  })
);

export default function ParticipantStrip() {
  const classes = useStyles();
  const { toolbarEnabled, setToolbarEnabled, sketchTouched, setSnackBar, setSnackBarText } = useContext(ToolBarContext);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const {
    room: { localParticipant },
    isSketchActive,
    currentUserOnSketchField,
    sendDataToParticipants,
    currentHostForSketch,
  } = useVideoContext();
  const participants = useParticipants();
  const [selectedParticipant, setSelectedParticipant] = useSelectedParticipant();
  const { userType } = useContext(MeetingContext);
  const [participantSwitchOver, setParticipantSwitchOver] = useState<boolean>(true);
  const [switchOverParticipant, setSwitchOverParticipant] = useState(null);
  const mainParticipant = useMainSpeaker();

  useEffect(() => {
    if (isSketchActive && currentUserOnSketchField !== null) {
      if (currentHostForSketch) {
        setSnackBar(true);
        setSnackBarText(`${currentHostForSketch.identity} has started Annotation`);
      }
      if (selectedParticipant === null || selectedParticipant.sid !== currentUserOnSketchField.sid) {
        localParticipant.sid === currentUserOnSketchField.sid
          ? setSelectedParticipant(localParticipant)
          : setSelectedParticipant(
              participants.find(participant => {
                if (participant.sid === currentUserOnSketchField.sid) {
                  return participant;
                }
              })
            );
      }
    }
  }, [isSketchActive, currentUserOnSketchField, currentHostForSketch]);

  const setMainVideo = participant => {
    if (toolbarEnabled) {
      if (!sketchTouched) {
        setSelectedParticipant(participant);
        sendDataToParticipants(
          JSON.stringify({
            type: 'ANNOTATION_START',
            data: {
              isSketchActive: true,
              currentUserOnSketchField: participant,
            },
          })
        );
      } else {
        if (mainParticipant.sid !== participant.sid) {
          setParticipantSwitchOver(!participantSwitchOver);
          setSwitchOverParticipant(participant);
        }
      }
    } else {
      setSelectedParticipant(participant);
    }
  };

  const switchParticipant = () => {
    setSelectedParticipant(switchOverParticipant);
    setSwitchOverParticipant(null);
    setToolbarEnabled(false);
  };

  return (
    <Container>
      <ScrollContainer>
        <Participant
          participant={localParticipant}
          isSelected={selectedParticipant === localParticipant}
          onClick={() => setMainVideo(localParticipant)}
        />
        {participants.map(participant => (
          <Participant
            key={participant.sid}
            participant={participant}
            isSelected={selectedParticipant === participant}
            onClick={() => setMainVideo(participant)}
          />
        ))}
        {userType === 'guest' ? null : (
          <Button
            className={classes.inviteButton}
            variant="contained"
            disabled={showInviteModal}
            onClick={() => setShowInviteModal(true)}
          >
            + SEND INVITE
          </Button>
        )}
        {showInviteModal ? <InviteModal showModal={showInviteModal} setShowModal={setShowInviteModal} /> : null}
        {!participantSwitchOver ? (
          <ParticipantSwitchOverModal
            showModal={!participantSwitchOver}
            setShowModal={setParticipantSwitchOver}
            switchParticipant={switchParticipant}
          />
        ) : null}
      </ScrollContainer>
    </Container>
  );
}
